import { BookIcon, SearchIcon, SyncIcon } from '@primer/octicons-react';
import { useRouter } from 'next/router';
import React, { ComponentProps, Suspense } from 'react';
import { graphql } from 'react-relay';

import Button from '../../components/core/Button';
import DialogButton from '../../components/core/DialogButton';
import EmptyState from '../../components/core/EmptyState';
import EnumPairCheckboxGroupField from '../../components/core/EnumPairCheckboxGroupField';
import EnumPairSearchOverlayTokenField from '../../components/core/EnumPairSearchOverlayTokenField';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import QueryFormik from '../../components/core/QueryFormik';
import Spinner from '../../components/core/Spinner';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import TextField from '../../components/core/TextField';
import View from '../../components/core/View';
import ScrapSourceBookCreateDialog from '../../components/scrapSource/ScrapSourceBookCreateDialog';
import ScrapSourceBookNamesSearchOverlayField from '../../components/scrapSource/ScrapSourceBookNamesSearchOverlayField';
import ScrapSourceConnectionDataTable from '../../components/scrapSource/ScrapSourceConnectionDataTable';
import ScrapSourceCreateDialog from '../../components/scrapSource/ScrapSourceCreateDialog';
import ScrapSourceWindowPaginator from '../../components/scrapSource/ScrapSourceWindowPaginator';
import UnitPaginatorSearchOverlayTokenField from '../../components/unit/UnitPaginatorSearchOverlayTokenField';
import UnitAPaginatorSearchOverlayTokenField from '../../components/unitA/UnitAPaginatorSearchOverlayTokenField';
import { usePaginationContext } from '../../contexts/PaginationContext';
import useInitialValuesFromParsedUrlQuery from '../../hooks/useInitialValuesFromParsedUrlQuery';
import useToast from '../../hooks/useToast';
import {
  scrapSource_scrapSourcesQuery,
  ScrapSourceExamCategoryEnum,
  ScrapSourceExamGradeEnum,
  ScrapSourceExamMonthEnum,
  ScrapSourceExamSourceEnum,
  ScrapSourceExamYearEnum,
  ScrapSourceStatusEnum,
  ScrapSourceTypeEnum,
} from '../../relay/__generated__/scrapSource_scrapSourcesQuery.graphql';
import { numberWithCommas } from '../../utils/number';
import { normalizeObject } from '../../utils/object';
import { NextPage } from '../_app';

import ScrapSourceId from './[scrapSourceId]';

const scrapSourcesForScrapSource = graphql`
  query scrapSource_scrapSourcesQuery(
    $filters: ScrapSourceFilter
    $order: ScrapSourceOrder
    $page: Int
    $pageSize: Int
  ) {
    ...ScrapSourceWindowPaginator_query @arguments(filters: $filters, order: $order, page: $page, pageSize: $pageSize)
  }
`;

type Props = {};

const ScrapSource: NextPage<Props> = () => {
  const router = useRouter();
  const { toast } = useToast();

  const { initialValues, setParsedUrlQuery } = useInitialValuesFromParsedUrlQuery({
    search: { type: 'string' },
    status_In: { type: 'string', multiple: true },
    type_In: { type: 'string', multiple: true },
    bookName_Contains: { type: 'string' },
    bookPublisher_Contains: { type: 'string' },
    category_In: { type: 'string', multiple: true },
    grade_In: { type: 'string', multiple: true },
    month_In: { type: 'string', multiple: true },
    source_In: { type: 'string', multiple: true },
    unitAId_In: { type: 'string', multiple: true },
    unitId_In: { type: 'string', multiple: true },
    year_In: { type: 'string', multiple: true },
    order: { type: 'string' },
    page: { type: 'number' },
  });

  const {
    search,
    status_In,
    order,
    page,
    type_In,
    bookName_Contains,
    bookPublisher_Contains,
    category_In,
    grade_In,
    month_In,
    source_In,
    unitAId_In,
    unitId_In,
    year_In,
  } = initialValues;

  const { pageSize } = usePaginationContext();

  return (
    <View>
      <Head siteTitle={'PDF 스크랩'} />
      <ErrorBoundary>
        <QueryFormik<scrapSource_scrapSourcesQuery>
          query={scrapSourcesForScrapSource}
          staticVariables={{ pageSize }}
          initialValues={{
            filters: {
              search,
              status_In: status_In as ScrapSourceStatusEnum[],
              type_In: type_In as ScrapSourceTypeEnum[],
              bookName_Contains,
              bookPublisher_Contains,
              category_In: category_In as ScrapSourceExamCategoryEnum[],
              grade_In: grade_In as ScrapSourceExamGradeEnum[],
              month_In: month_In as ScrapSourceExamMonthEnum[],
              source_In: source_In as ScrapSourceExamSourceEnum[],
              unitAId_In,
              unitId_In,
              year_In: year_In as ScrapSourceExamYearEnum[],
            },
            order: order || '-created',
            page,
          }}
          options={{ fetchPolicy: 'store-and-network' }}
          onSubmit={(values) => setParsedUrlQuery({ ...values.filters, order }, { scroll: false })}
          enableReinitialize
        >
          {({ values: { filters, order }, setFieldValue, submitForm, handleSubmit, resetForm }, queryReference) => {
            const handleReset = () => {
              resetForm({ values: { order } });
              setParsedUrlQuery({ order }, { scroll: false });
            };

            const handleChangeOrderActionMenu: ComponentProps<typeof QueryFormik.OrderActionMenuButton>['onChange'] = (
              newValue,
            ) => {
              setParsedUrlQuery({ ...filters, order: newValue }, { scroll: false });
            };

            return (
              <View>
                <Grid gapX={2} sx={{ alignItems: 'center' }}>
                  <Grid.Unit size={'max'}>
                    <Text as={'h1'}>PDF 스크랩</Text>
                  </Grid.Unit>
                  <Grid.Unit size={'min'}>
                    <DialogButton
                      size={'large'}
                      variant={'outline'}
                      renderDialog={({ isOpen, closeDialog }) => (
                        <ScrapSourceBookCreateDialog
                          isOpen={isOpen}
                          onDismiss={closeDialog}
                          config={{
                            onCompleted: () => {
                              toast('가져오기를 성공했어요', 'success');
                              handleSubmit();
                              closeDialog();
                            },
                          }}
                        />
                      )}
                    >
                      시중 교재 스크랩에서 가져오기
                    </DialogButton>
                  </Grid.Unit>
                  <Grid.Unit size={'min'}>
                    <DialogButton
                      size={'large'}
                      variant={'primary'}
                      renderDialog={({ isOpen, closeDialog }) => (
                        <ScrapSourceCreateDialog
                          isOpen={isOpen}
                          onDismiss={closeDialog}
                          wide
                          config={{
                            onCompleted: () => {
                              toast('PDF 업로드에 성공했어요', 'success');
                              handleSubmit();
                              closeDialog();
                            },
                          }}
                        />
                      )}
                    >
                      PDF 업로드
                    </DialogButton>
                  </Grid.Unit>
                </Grid>
                <Grid>
                  <Grid.Unit size={'min'}>
                    <QueryFormik.FilterSearchTextField
                      typename={'ScrapSourceFilter'}
                      label={'Search'}
                      labelConfig={{ visuallyHidden: true }}
                      name={'filters.search'}
                      size={'large'}
                      autoComplete={'off'}
                      leadingVisual={SearchIcon}
                      debounce
                      onChange={(e) => {
                        setFieldValue('filters.search', e.target.value);
                        setTimeout(() => submitForm(), 0);
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={'max'}>
                    <View sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Stack gapX={2}>
                        <Stack.Item>
                          <Button leadingIcon={SyncIcon} variant={'plain'} onClick={handleReset}>
                            초기화
                          </Button>
                        </Stack.Item>
                        <Stack.Item>
                          <QueryFormik.OrderActionMenuButton
                            typename={'ScrapSourceOrder'}
                            orders={['created']}
                            onChange={handleChangeOrderActionMenu}
                          />
                        </Stack.Item>
                      </Stack>
                    </View>
                  </Grid.Unit>
                </Grid>
                <View sx={{ marginTop: 5 }}>
                  <QueryFormik.FilterGrid
                    onChange={(filters) => {
                      setParsedUrlQuery({ ...filters, order }, { scroll: false });
                    }}
                  >
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'상태'}>
                      <EnumPairSearchOverlayTokenField
                        typename={'ScrapSourceStatusEnum'}
                        label={'상태'}
                        name={'filters.status_In'}
                        placeholder={'상태 선택'}
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'교육과정'}>
                      <UnitPaginatorSearchOverlayTokenField
                        label={'units'}
                        name={'filters.unitId_In'}
                        placeholder={'교육과정 선택'}
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={1} title={'기출/사설'}>
                      <EnumPairCheckboxGroupField
                        typename={'ScrapSourceTypeEnum'}
                        label={'타입'}
                        name={'filters.type_In'}
                        renderContainer={(children) => (
                          <View sx={{ paddingX: 2 }}>
                            <Grid gapX={2}>{children}</Grid>
                          </View>
                        )}
                        renderOptionWrapper={(children, { id }) => (
                          <Grid.Unit key={id} size={'min'}>
                            {children}
                          </Grid.Unit>
                        )}
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={1} title={'기출 상세'}>
                      <Grid>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <EnumPairSearchOverlayTokenField
                            typename={'ScrapSourceExamSourceEnum'}
                            label={'출제기관'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.source_In'}
                            placeholder={'출제기관 선택'}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <EnumPairSearchOverlayTokenField
                            typename={'ScrapSourceExamYearEnum'}
                            label={'연도'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.year_In'}
                            placeholder={'연도 선택'}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <EnumPairSearchOverlayTokenField
                            typename={'ScrapSourceExamMonthEnum'}
                            label={'월'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.month_In'}
                            placeholder={'월 선택'}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <EnumPairSearchOverlayTokenField
                            typename={'ScrapSourceExamGradeEnum'}
                            label={'학년'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.grade_In'}
                            placeholder={'학년 선택'}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <EnumPairSearchOverlayTokenField
                            typename={'ScrapSourceExamCategoryEnum'}
                            label={'구분'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.category_In'}
                            placeholder={'구분 선택'}
                          />
                        </Grid.Unit>
                      </Grid>
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={1} title={'사설 상세'}>
                      <Grid>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <ScrapSourceBookNamesSearchOverlayField
                            label={'출처'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.bookName_Contains'}
                            placeholder={'출처 검색'}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <UnitAPaginatorSearchOverlayTokenField
                            label={'unitA'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.unitAId_In'}
                            placeholder={'Unit A 선택'}
                            variables={{
                              order: { order: 'ASC' },
                            }}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={[1, 1, 1 / 5]}>
                          <TextField
                            label={'출판사'}
                            labelConfig={{ visuallyHidden: true }}
                            name={'filters.bookPublisher_Contains'}
                            placeholder={'출판사 검색'}
                            trailingVisual={SearchIcon}
                            debounce
                          />
                        </Grid.Unit>
                      </Grid>
                    </QueryFormik.FilterGridUnit>
                  </QueryFormik.FilterGrid>
                </View>
                <View sx={{ marginTop: 5 }}>
                  <ErrorBoundary key={queryReference?.fetchKey}>
                    <Suspense fallback={<Spinner />}>
                      <QueryFormik.PreloadedQueryRenderer<scrapSource_scrapSourcesQuery>>
                        {(queryReference) => (
                          <ScrapSourceWindowPaginator
                            fragmentReference={queryReference}
                            onLoadPage={(page) => setParsedUrlQuery({ ...filters, order, page })}
                          >
                            {({ scrapSources }, { renderPagination }) => (
                              <>
                                <View>
                                  <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                                    총 {numberWithCommas(scrapSources.totalCount || 0)}개
                                  </Text>
                                </View>
                                <View sx={{ marginTop: 3 }}>
                                  <ScrapSourceConnectionDataTable
                                    scrapSourceConnection={scrapSources}
                                    onRowClick={({ id }) => {
                                      const pathname = `/scrapSource/${id}`;
                                      router.push(
                                        { pathname, query: normalizeObject({ ...filters, order }) },
                                        pathname,
                                      );
                                    }}
                                    emptyState={
                                      <View sx={{ paddingY: 3 }}>
                                        <EmptyState
                                          title={'필터에 맞는 결과가 없어요'}
                                          description={'다른 필터로 다시 시도해보세요.'}
                                        />
                                      </View>
                                    }
                                  />
                                </View>
                                <View sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
                                  {renderPagination?.()}
                                </View>
                              </>
                            )}
                          </ScrapSourceWindowPaginator>
                        )}
                      </QueryFormik.PreloadedQueryRenderer>
                    </Suspense>
                  </ErrorBoundary>
                </View>
              </View>
            );
          }}
        </QueryFormik>
      </ErrorBoundary>
    </View>
  );
};

ScrapSource.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
ScrapSource.authenticated = true;
ScrapSource.routes = [
  {
    id: 'scrapSource',
    name: 'PDF 스크랩',
    icon: BookIcon,
    pathname: '/scrapSource',
    permissions: ['scrap_source_read'],
  },
  ...ScrapSourceId.routes,
];

export default ScrapSource;
